import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import { useSpinDelay } from 'spin-delay';

import { Spinner } from '~components/generic/spinner/Spinner';
import useLabels, { useLabel } from '~hooks/useLabels';
import { ViewContext } from '~modules/media-library/model/ViewContext';
import { CheckboxButton } from '~modules/media-library/view/Checkbox';
import { SortDropdown } from '~modules/media-library/view/SortDropdown/SortDropdown';
import { ViewVariantSlider } from '~modules/media-library/view/ViewVariantSlider/ViewVariantSlider';

export function ResultHeader({
  assetCount,
  showFavouritesOnly = false,
  showFavouritesToggle = true,
  viewContext = ViewContext.DEFAULT,
  onToggleShowFavouritesOnly,
  sortOptions,
  sortOrder,
  sortKey,
  onSort,
  viewVariant,
  viewVariants,
  onChangeView,
  isLoading = false,
}: {
  assetCount?: number;
  // Favourites
  showFavouritesOnly?: boolean;
  showFavouritesToggle?: boolean;
  viewContext?: string;
  onToggleShowFavouritesOnly?: () => void;
  // Sorting
  sortOptions?: ComponentProps<typeof SortDropdown>['options'];
  sortKey?: ComponentProps<typeof SortDropdown>['sortKey'];
  sortOrder?: ComponentProps<typeof SortDropdown>['sortOrder'];
  onSort?: ComponentProps<typeof SortDropdown>['onChange'];
  // View
  viewVariant?: ComponentProps<typeof ViewVariantSlider>['value'];
  viewVariants?: ComponentProps<typeof ViewVariantSlider>['variants'];
  onChangeView?: ComponentProps<typeof ViewVariantSlider>['onChange'];
  // states
  isLoading?: boolean;
}) {
  const [msgCount, setMsgCount] = useState<string>(undefined);

  // Labels
  const { btnMediaLibraryFavouritesOnly } = useLabels(['btnMediaLibraryFavouritesOnly']);
  const labelMediaLibrarySearchEmpty = useLabel('labelMediaLibrarySearchEmpty');
  const msgMediaLibraryResultCount = useLabel('msgMediaLibraryResultCount', { count: assetCount });

  useEffect(() => {
    setMsgCount(
      viewContext === ViewContext.SEARCH && assetCount === 0
        ? labelMediaLibrarySearchEmpty
        : msgMediaLibraryResultCount,
    );
  }, [assetCount, labelMediaLibrarySearchEmpty, msgMediaLibraryResultCount, viewContext]);

  // Derived values
  const showSpinner = useSpinDelay(isLoading);
  const showFavourites = Boolean(onToggleShowFavouritesOnly) && showFavouritesToggle;
  const showSort = Boolean(sortOptions) && Boolean(onSort);
  const showViewSlider = Boolean(viewVariant) && Boolean(viewVariants) && Boolean(onChangeView);

  return (
    <div className="ml--result-header bg-gray bg-opacity-80 backdrop-filter backdrop-blur px-3">
      <div className="relative flex items-center justify-between mx-auto max-w-screen-xl bg-opacity-100">
        <div className="select-none">{showSpinner ? <Spinner /> : assetCount >= 0 ? msgCount : ''}</div>
        <div className="flex gap-8">
          {showFavourites && (
            <CheckboxButton
              isChecked={showFavouritesOnly}
              onClick={onToggleShowFavouritesOnly}
              className="bg-transparent"
            >
              {btnMediaLibraryFavouritesOnly}
            </CheckboxButton>
          )}
          {showSort && <SortDropdown options={sortOptions} sortKey={sortKey} sortOrder={sortOrder} onChange={onSort} />}
          {showViewSlider && <ViewVariantSlider value={viewVariant} variants={viewVariants} onChange={onChangeView} />}
        </div>
      </div>
    </div>
  );
}
